import React, { useMemo, useEffect, useCallback, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PracticeContentLayout, { TopicItem } from '../../components/common/PracticeContentLayout';
import ArticleRating from '../../components/common/ArticleRating';
import CommentSection from '../../components/common/CommentSection';
import { ArticleHeaderProvider } from '../../context/ArticleHeaderContext';
import { useSqlProgress } from '../../hooks/useSqlProgress';
import { allProblems } from '../../data/sql-problems';
import { ProblemTopic } from '../../data/sql-problems/types';

// Lazy-loaded lesson components (SQL Concepts)
const SQLWhatIsDatabase = React.lazy(() => import('./sql/SQLWhatIsDatabase'));
const SQLTablesSchemas = React.lazy(() => import('./sql/SQLTablesSchemas'));
const SQLDataTypes = React.lazy(() => import('./sql/SQLDataTypes'));
const SQLNormalization = React.lazy(() => import('./sql/SQLNormalization'));
const SQLKeysRelationships = React.lazy(() => import('./sql/SQLKeysRelationships'));
const SQLIndexing = React.lazy(() => import('./sql/SQLIndexing'));
const SQLAcidTransactions = React.lazy(() => import('./sql/SQLAcidTransactions'));
const SQLSelectBasics = React.lazy(() => import('./sql/SQLSelectBasics'));
const SQLWhereFiltering = React.lazy(() => import('./sql/SQLWhereFiltering'));
const SQLJoins = React.lazy(() => import('./sql/SQLJoins'));
const SQLGroupByHaving = React.lazy(() => import('./sql/SQLGroupByHaving'));
const SQLOrderByLimit = React.lazy(() => import('./sql/SQLOrderByLimit'));
const SQLInsertUpdateDelete = React.lazy(() => import('./sql/SQLInsertUpdateDelete'));
const SQLSubqueries = React.lazy(() => import('./sql/SQLSubqueries'));

// Lazy-loaded playground
const SQLPlaygroundPage = React.lazy(() => import('./sql/SQLPlaygroundPage'));

// Lazy-loaded practice problem pages
const SQLProblemSelect = React.lazy(() => import('./sql/SQLProblemSelect'));
const SQLProblemWhere = React.lazy(() => import('./sql/SQLProblemWhere'));
const SQLProblemJoins = React.lazy(() => import('./sql/SQLProblemJoins'));
const SQLProblemGroupBy = React.lazy(() => import('./sql/SQLProblemGroupBy'));
const SQLProblemSubqueries = React.lazy(() => import('./sql/SQLProblemSubqueries'));
const SQLProblemDML = React.lazy(() => import('./sql/SQLProblemDML'));

// Lazy-loaded problem solver
const SQLProblemSolver = React.lazy(() => import('./sql/SQLProblemSolver'));

interface SQLContent {
  id: string;
  title: string;
  lastUpdated: string;
  author: {
    name: string;
    fullName: string;
    avatar?: string;
  };
  readTime: string;
  content: React.ReactNode;
}

// Mapping of problem category IDs to ProblemTopic for filtering
const categoryToTopic: Record<string, ProblemTopic> = {
  'problems-select': 'select',
  'problems-where': 'where',
  'problems-joins': 'joins',
  'problems-groupby': 'groupby',
  'problems-subqueries': 'subqueries',
  'problems-dml': 'dml',
};

// Lesson topic IDs (used for progress calculations)
const lessonTopicIds = [
  'what-is-a-database',
  'tables-and-schemas',
  'data-types',
  'normalization',
  'keys-and-relationships',
  'indexing-basics',
  'acid-transactions',
  'select-basics',
  'where-filtering',
  'joins',
  'group-by-having',
  'order-by-limit',
  'insert-update-delete',
  'basic-subqueries',
];

// Static topic structure (completed state is computed dynamically)
const baseTopics: TopicItem[] = [
  {
    id: 'sql-concepts',
    title: 'SQL Concepts',
    completed: false,
    children: [
      { id: 'what-is-a-database', title: 'What is a Database', completed: false },
      { id: 'tables-and-schemas', title: 'Tables & Schemas', completed: false },
      { id: 'data-types', title: 'SQL Data Types', completed: false },
      { id: 'normalization', title: 'Normalization Basics', completed: false },
      { id: 'keys-and-relationships', title: 'Keys & Relationships', completed: false },
      { id: 'indexing-basics', title: 'Basic Indexing Concepts', completed: false },
      { id: 'acid-transactions', title: 'ACID & Transactions', completed: false },
      { id: 'select-basics', title: 'SELECT Basics', completed: false },
      { id: 'where-filtering', title: 'WHERE & Filtering', completed: false },
      { id: 'joins', title: 'JOINs (INNER/LEFT/RIGHT)', completed: false },
      { id: 'group-by-having', title: 'GROUP BY & HAVING', completed: false },
      { id: 'order-by-limit', title: 'ORDER BY & LIMIT', completed: false },
      { id: 'insert-update-delete', title: 'INSERT/UPDATE/DELETE', completed: false },
      { id: 'basic-subqueries', title: 'Basic Subqueries', completed: false },
    ],
  },
  {
    id: 'playground',
    title: 'SQL Playground',
    completed: false,
  },
  {
    id: 'practice-problems',
    title: 'Practice Problems',
    completed: false,
    children: [
      { id: 'problems-select', title: 'SELECT Problems', completed: false },
      { id: 'problems-where', title: 'WHERE & Filtering', completed: false },
      { id: 'problems-joins', title: 'JOIN Problems', completed: false },
      { id: 'problems-groupby', title: 'GROUP BY & Aggregation', completed: false },
      { id: 'problems-subqueries', title: 'Subquery Problems', completed: false },
      { id: 'problems-dml', title: 'INSERT/UPDATE/DELETE', completed: false },
    ],
  },
];

// Helper function to find first leaf topic
const findFirstLeafTopic = (items: TopicItem[]): string | null => {
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      const childResult = findFirstLeafTopic(item.children);
      if (childResult) return childResult;
    } else {
      return item.id;
    }
  }
  return null;
};

const SQLPractice: React.FC = () => {
  const { topicId } = useParams<{ topicId?: string }>();
  const navigate = useNavigate();
  const { isLessonComplete, isProblemSolved, markLessonComplete } = useSqlProgress();

  // Compute topics with progress-based completed state
  const topics: TopicItem[] = useMemo(() => {
    // Helper: check if all problems in a category are solved
    const isCategoryComplete = (categoryId: string): boolean => {
      const topic = categoryToTopic[categoryId];
      if (!topic) return false;
      const categoryProblems = allProblems.filter((p) => p.topicTags.includes(topic));
      if (categoryProblems.length === 0) return false;
      return categoryProblems.every((p) => isProblemSolved(p.id));
    };

    return baseTopics.map((section) => {
      if (!section.children) return section;

      const updatedChildren = section.children.map((child) => {
        // For lesson topics
        if (lessonTopicIds.includes(child.id)) {
          return { ...child, completed: isLessonComplete(child.id) };
        }
        // For problem categories
        if (categoryToTopic[child.id]) {
          return { ...child, completed: isCategoryComplete(child.id) };
        }
        return child;
      });

      // A parent section is completed when all its children are completed
      const allChildrenComplete = updatedChildren.every((c) => c.completed);
      return { ...section, children: updatedChildren, completed: allChildrenComplete };
    });
  }, [isLessonComplete, isProblemSolved]);

  // Redirect to first topic if no topicId is provided
  useEffect(() => {
    if (!topicId) {
      const firstTopic = findFirstLeafTopic(topics);
      if (firstTopic) {
        navigate(`/languages/sql/${firstTopic}`, { replace: true });
      }
    }
  }, [topicId, navigate, topics]);

  // Calculate total chapters
  const calculateTotalChapters = useCallback((items: TopicItem[]): number => {
    let count = 0;
    for (const item of items) {
      if (!item.children || item.children.length === 0) {
        count++;
      } else {
        count += calculateTotalChapters(item.children);
      }
    }
    return count;
  }, []);

  const totalChapters = useMemo(() => calculateTotalChapters(topics), [calculateTotalChapters, topics]);

  // Calculate completed chapters from progress data
  const completedChapters = useMemo(() => {
    let count = 0;
    for (const section of topics) {
      if (!section.children) {
        if (section.completed) count++;
      } else {
        for (const child of section.children) {
          if (child.completed) count++;
        }
      }
    }
    return count;
  }, [topics]);

  // Get current topic ID
  const currentTopicId = topicId || findFirstLeafTopic(topics) || 'what-is-a-database';

  // Check if current topic is a valid topic, if not redirect
  useEffect(() => {
    if (!topicId) return;
    // Allow solve- prefix and all known topics
    if (topicId.startsWith('solve-')) return;
    // Check if topicId exists in topics tree
    const isValid = (items: TopicItem[]): boolean => {
      for (const item of items) {
        if (item.id === topicId) return true;
        if (item.children && isValid(item.children)) return true;
      }
      return false;
    };
    if (!isValid(baseTopics)) {
      const firstTopic = findFirstLeafTopic(topics);
      if (firstTopic) {
        navigate(`/languages/sql/${firstTopic}`, { replace: true });
      }
    }
  }, [topicId, navigate, topics]);

  // Content for different topics
  const getContent = (id: string): SQLContent => {
    const authorInfo = {
      name: 'Sachin',
      fullName: 'Sachin Saini',
      avatar: undefined,
    };

    const loadingFallback = (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );

    // Mark Complete button for lessons — same style as System Design pages
    const MarkCompleteButton: React.FC<{ lessonId: string }> = ({ lessonId }) => {
      const completed = isLessonComplete(lessonId);
      return (
        <button
          onClick={() => {
            if (completed) {
              markLessonComplete(`__UNCOMPLETE__${lessonId}`);
            } else {
              markLessonComplete(lessonId);
            }
          }}
          className={`article-completion-button ${completed ? 'is-complete' : 'is-incomplete'} inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors`}
        >
          {completed ? (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Completed ✓
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Mark as Complete
            </>
          )}
        </button>
      );
    };

    // Helper to build lesson content — same structure as DynamicContentPage (System Design)
    const buildLessonContent = (
      lessonId: string,
      LessonComponent: React.ComponentType<{ author: typeof authorInfo }>,
      articleSlug: string
    ) => (
      <Suspense fallback={loadingFallback}>
        <div className="max-w-none">
          <LessonComponent author={authorInfo} />
          {/* Article Rating + Mark Complete — same as System Design pages */}
          <div className="mt-12">
            <ArticleRating articleId={articleSlug} />
            <div className="border-t border-gray-200 dark:border-gray-700 mt-6 pt-6">
              <MarkCompleteButton lessonId={lessonId} />
            </div>
          </div>
          {/* Comment Section */}
          <div className="mt-12">
            <CommentSection articleId={articleSlug} />
          </div>
        </div>
      </Suspense>
    );

    const contents: Record<string, SQLContent> = {
      'what-is-a-database': {
        id: 'what-is-a-database',
        title: 'What is a Database',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '8 min read',
        content: buildLessonContent('what-is-a-database', SQLWhatIsDatabase, 'sql-what-is-a-database'),
      },
      'tables-and-schemas': {
        id: 'tables-and-schemas',
        title: 'Tables & Schemas',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '10 min read',
        content: buildLessonContent('tables-and-schemas', SQLTablesSchemas, 'sql-tables-and-schemas'),
      },
      'data-types': {
        id: 'data-types',
        title: 'SQL Data Types',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '9 min read',
        content: buildLessonContent('data-types', SQLDataTypes, 'sql-data-types'),
      },
      'normalization': {
        id: 'normalization',
        title: 'Normalization Basics',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '12 min read',
        content: buildLessonContent('normalization', SQLNormalization, 'sql-normalization'),
      },
      'keys-and-relationships': {
        id: 'keys-and-relationships',
        title: 'Keys & Relationships',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '11 min read',
        content: buildLessonContent('keys-and-relationships', SQLKeysRelationships, 'sql-keys-and-relationships'),
      },
      'indexing-basics': {
        id: 'indexing-basics',
        title: 'Basic Indexing Concepts',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '10 min read',
        content: buildLessonContent('indexing-basics', SQLIndexing, 'sql-indexing-basics'),
      },
      'acid-transactions': {
        id: 'acid-transactions',
        title: 'ACID & Transactions',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '11 min read',
        content: buildLessonContent('acid-transactions', SQLAcidTransactions, 'sql-acid-transactions'),
      },
      'select-basics': {
        id: 'select-basics',
        title: 'SELECT Basics',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '10 min read',
        content: buildLessonContent('select-basics', SQLSelectBasics, 'sql-select-basics'),
      },
      'where-filtering': {
        id: 'where-filtering',
        title: 'WHERE & Filtering',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '10 min read',
        content: buildLessonContent('where-filtering', SQLWhereFiltering, 'sql-where-filtering'),
      },
      'joins': {
        id: 'joins',
        title: 'JOINs (INNER/LEFT/RIGHT)',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '14 min read',
        content: buildLessonContent('joins', SQLJoins, 'sql-joins'),
      },
      'group-by-having': {
        id: 'group-by-having',
        title: 'GROUP BY & HAVING',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '12 min read',
        content: buildLessonContent('group-by-having', SQLGroupByHaving, 'sql-group-by-having'),
      },
      'order-by-limit': {
        id: 'order-by-limit',
        title: 'ORDER BY & LIMIT',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '9 min read',
        content: buildLessonContent('order-by-limit', SQLOrderByLimit, 'sql-order-by-limit'),
      },
      'insert-update-delete': {
        id: 'insert-update-delete',
        title: 'INSERT/UPDATE/DELETE',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '12 min read',
        content: buildLessonContent('insert-update-delete', SQLInsertUpdateDelete, 'sql-insert-update-delete'),
      },
      'basic-subqueries': {
        id: 'basic-subqueries',
        title: 'Basic Subqueries',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '13 min read',
        content: buildLessonContent('basic-subqueries', SQLSubqueries, 'sql-basic-subqueries'),
      },
      // SQL Playground
      'playground': {
        id: 'playground',
        title: 'SQL Playground',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense fallback={loadingFallback}>
            <SQLPlaygroundPage />
          </Suspense>
        ),
      },
      // Practice Problems
      'problems-select': {
        id: 'problems-select',
        title: 'SELECT Problems',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense fallback={loadingFallback}>
            <SQLProblemSelect />
          </Suspense>
        ),
      },
      'problems-where': {
        id: 'problems-where',
        title: 'WHERE & Filtering',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense fallback={loadingFallback}>
            <SQLProblemWhere />
          </Suspense>
        ),
      },
      'problems-joins': {
        id: 'problems-joins',
        title: 'JOIN Problems',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense fallback={loadingFallback}>
            <SQLProblemJoins />
          </Suspense>
        ),
      },
      'problems-groupby': {
        id: 'problems-groupby',
        title: 'GROUP BY & Aggregation',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense fallback={loadingFallback}>
            <SQLProblemGroupBy />
          </Suspense>
        ),
      },
      'problems-subqueries': {
        id: 'problems-subqueries',
        title: 'Subquery Problems',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense fallback={loadingFallback}>
            <SQLProblemSubqueries />
          </Suspense>
        ),
      },
      'problems-dml': {
        id: 'problems-dml',
        title: 'INSERT/UPDATE/DELETE',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense fallback={loadingFallback}>
            <SQLProblemDML />
          </Suspense>
        ),
      },
    };

    // Handle solve-<problemId> pattern for the problem solver
    if (id.startsWith('solve-')) {
      const problemId = id.slice(6); // remove 'solve-' prefix
      return {
        id,
        title: 'Solve Problem',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense fallback={loadingFallback}>
            <SQLProblemSolver problemId={problemId} />
          </Suspense>
        ),
      };
    }

    return contents[id] || contents['what-is-a-database'];
  };

  const handleTopicSelect = (selectedTopicId: string) => {
    navigate(`/languages/sql/${selectedTopicId}`);
  };

  const currentContent = getContent(currentTopicId);

  return (
    <PracticeContentLayout
      className="system-design-article-page language-article-page"
      title="Master SQL"
      totalChapters={totalChapters}
      completedChapters={completedChapters}
      topics={topics}
      currentTopicId={currentTopicId}
      onTopicSelect={handleTopicSelect}
      onToggleComplete={(topicId, completed) => {
        if (completed) {
          markLessonComplete(topicId);
        } else {
          markLessonComplete(`__UNCOMPLETE__${topicId}`);
        }
      }}
      content={
        <ArticleHeaderProvider articleId={`sql:${currentTopicId}`}>
          <React.Fragment key={currentTopicId}>{currentContent.content}</React.Fragment>
        </ArticleHeaderProvider>
      }
    />
  );
};

export default SQLPractice;
